<template>
  <div class="manage-overlay">
    <div class="manage-bar">
      <span class="manage-title">订单管理</span>
      <a-button size="small" @click="$emit('close')">返回桌面</a-button>
    </div>
    <div class="manage-wrap">
      <!-- 筛选：状态 / 类型 / 日期（后端 GET /order/list 分页） -->
      <div class="manage-filters">
        <span class="f-label">状态</span>
        <a-select v-model="queryStatus" style="width: 110px" @change="reload">
          <a-select-option value="">全部</a-select-option>
          <a-select-option value="pending">开台中</a-select-option>
          <a-select-option value="ordered">已下单</a-select-option>
          <a-select-option value="parked">挂单中</a-select-option>
          <a-select-option value="settled">已结算</a-select-option>
          <a-select-option value="cancelled">已取消</a-select-option>
          <a-select-option value="refund">已退款</a-select-option>
        </a-select>
        <span class="f-label">类型</span>
        <a-select v-model="queryOrderType" style="width: 90px" @change="reload">
          <a-select-option value="">全部</a-select-option>
          <a-select-option value="dine_in">堂食</a-select-option>
          <a-select-option value="takeout">外带</a-select-option>
        </a-select>
        <span class="f-label">日期</span>
        <a-range-picker @change="onDateChange" />
        <a-button type="primary" icon="search" :loading="loading" @click="reload">查询</a-button>
      </div>
      <a-table :columns="columns" :data-source="rows" :loading="loading" rowKey="id"
               size="middle" :pagination="pagination" @change="handleTableChange">
        <template slot="type" slot-scope="text">
          {{ text === 'dine_in' ? '堂食' : '外带' }}
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="statusColor(text)">{{ statusText(text) }}</a-tag>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-button size="small" @click="showDetail(record)">详情</a-button>
          <a-button size="small" class="act-btn" @click="showPrint(record)">厨打</a-button>
          <!-- 退款按钮：2904 餐饮订单 push_btn '8' 权限位 + 已结算订单 -->
          <a-popconfirm v-if="canRefund && record.status === 'settled'" title="确认整单退款？"
                        @confirm="handleRefund(record)">
            <a-button size="small" class="act-btn" type="danger">退款</a-button>
          </a-popconfirm>
        </template>
      </a-table>
    </div>

    <!-- 订单详情弹窗 -->
    <a-modal :visible="detailVisible" title="订单详情" :footer="null" width="720px"
             @cancel="detailVisible = false">
      <div class="detail-grid" v-if="detailData">
        <div class="d-item"><span class="d-k">订单号</span>{{ detailData.order_no }}</div>
        <div class="d-item"><span class="d-k">状态</span>{{ statusText(detailData.status) }}</div>
        <div class="d-item"><span class="d-k">类型</span>{{ detailData.order_type === 'dine_in' ? '堂食' : '外带' }}</div>
        <div class="d-item"><span class="d-k">桌台</span>{{ detailData.tableName || '-' }}</div>
        <div class="d-item"><span class="d-k">会员</span>{{ detailData.memberName || '-' }}</div>
        <div class="d-item"><span class="d-k">人数</span>{{ detailData.person_count || '-' }}</div>
        <div class="d-item"><span class="d-k">合计</span>¥{{ fmt(detailData.total_amount) }}</div>
        <div class="d-item"><span class="d-k">优惠</span>¥{{ fmt(detailData.discount_amount) }}</div>
        <div class="d-item"><span class="d-k">抹零</span>¥{{ fmt(detailData.erase_amount) }}</div>
        <div class="d-item"><span class="d-k">实付</span>¥{{ fmt(detailData.pay_amount) }}</div>
        <div class="d-item"><span class="d-k">支付</span>{{ payText(detailData.pay_type) }}</div>
        <div class="d-item"><span class="d-k">收银员</span>{{ detailData.creatorName || '-' }}</div>
        <div class="d-item"><span class="d-k">下单时间</span>{{ detailData.create_time }}</div>
        <div class="d-item"><span class="d-k">结算时间</span>{{ detailData.settle_time || '-' }}</div>
      </div>
      <a-table v-if="detailData" :columns="itemColumns" :data-source="detailData.items || []"
               rowKey="id" size="small" :pagination="false" :scroll="{ y: 260 }" />
    </a-modal>

    <!-- 厨打/结账单打印数据（JSON 展示） -->
    <a-modal :visible="printVisible" title="厨打 / 结账单数据" :footer="null" width="560px"
             @cancel="printVisible = false">
      <pre class="print-pre">{{ printText }}</pre>
    </a-modal>
  </div>
</template>

<script>
import { orderList, orderDetail, orderPrintData, orderRefund } from '@/api/plugin/dining'
import { getUserBtnByCurrentUser } from '@/api/api'

const ORDER_URL = '/plugin/dining/order' // 餐饮订单功能 2904 的菜单 url

/** 订单管理视图（DiningBoard 子组件）：分页列表 + 筛选 + 详情 + 厨打 + 整单退款（权限位控制） */
export default {
  name: 'DiningOrderManage',
  props: {
    storeId: { type: [Number, String], required: true }
  },
  data() {
    return {
      queryStatus: '',
      queryOrderType: '',
      dateBegin: '',
      dateEnd: '',
      rows: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      loading: false,
      canRefund: false,
      detailVisible: false,
      detailData: null,
      printVisible: false,
      printText: '',
      columns: [
        { title: '订单号', dataIndex: 'order_no', width: 165 },
        { title: '类型', dataIndex: 'order_type', width: 65, scopedSlots: { customRender: 'type' } },
        { title: '桌台', dataIndex: 'tableName', width: 85, customRender: t => t || '外带' },
        { title: '会员', dataIndex: 'memberName', width: 85, customRender: t => t || '-' },
        { title: '状态', dataIndex: 'status', width: 88, scopedSlots: { customRender: 'status' } },
        { title: '合计', dataIndex: 'total_amount', width: 88, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '实付', dataIndex: 'pay_amount', width: 88, align: 'right', customRender: v => v === null || v === undefined || v === '' ? '-' : '¥' + this.fmt(v) },
        { title: '支付', dataIndex: 'pay_type', width: 70, customRender: t => this.payText(t) },
        { title: '收银员', dataIndex: 'creatorName', width: 85, customRender: t => t || '-' },
        { title: '下单时间', dataIndex: 'create_time', width: 155 },
        { title: '操作', key: 'action', width: 195, scopedSlots: { customRender: 'action' } }
      ],
      itemColumns: [
        { title: '菜品', dataIndex: 'material_name', width: 130 },
        { title: '选项', key: 'options', width: 150, customRender: (t, r) => this.optionsText(r.options_json) },
        { title: '单价', dataIndex: 'unit_price', width: 80, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '数量', dataIndex: 'quantity', width: 70, align: 'right' },
        { title: '小计', dataIndex: 'all_price', width: 88, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '标记', dataIndex: 'item_status', width: 70, customRender: t => t === 'added' ? '加菜' : '首单' },
        { title: '备注', dataIndex: 'remark', width: 90, customRender: t => t || '-' }
      ]
    }
  },
  computed: {
    pagination() {
      return {
        current: this.currentPage,
        pageSize: this.pageSize,
        total: this.total,
        showSizeChanger: false,
        showTotal: t => `共 ${t} 条`
      }
    }
  },
  created() {
    this.loadRefundPermission()
    this.load()
  },
  methods: {
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    statusText(s) {
      return { pending: '开台中', ordered: '已下单', parked: '挂单中', settled: '已结算', cancelled: '已取消', refund: '已退款' }[s] || s
    },
    statusColor(s) {
      return { pending: 'orange', ordered: 'blue', parked: 'purple', settled: 'green', cancelled: '', refund: 'red' }[s] || ''
    },
    payText(t) {
      if (!t) return '-'
      return { cash: '现金', wx: '微信', ali: '支付宝' }[t] || t
    },
    // 明细 options_json 快照转展示文本（valueName 以 / 连接，坏数据容忍）
    optionsText(json) {
      if (!json) return '-'
      try {
        const arr = JSON.parse(String(json))
        if (!Array.isArray(arr)) return '-'
        return arr.map(o => o.valueName).filter(Boolean).join('/') || '-'
      } catch (e) {
        return '-'
      }
    },
    /**
     * 退款权限位：GET /user/getUserBtnByCurrentUser → data.userBtn = [{url, btnStr}]。
     * admin 账号无 userBtn 字段视为全权限；否则找 2904（/plugin/dining/order）的 btnStr 含 '8'。
     * （findMenuByPNumber 菜单结构不含 pushBtn/number 字段，故用此接口）
     */
    loadRefundPermission() {
      getUserBtnByCurrentUser().then(res => {
        const btnList = res && res.data && res.data.userBtn
        if (!btnList) {
          this.canRefund = true
          return
        }
        this.canRefund = btnList.some(item => item.url === ORDER_URL &&
          item.btnStr && item.btnStr.indexOf('8') > -1)
      }).catch(() => {
        this.canRefund = false
      })
    },
    onDateChange(dates, dateStrings) {
      this.dateBegin = (dateStrings && dateStrings[0]) || ''
      this.dateEnd = (dateStrings && dateStrings[1]) || ''
    },
    reload() {
      this.currentPage = 1
      this.load()
    },
    load() {
      this.loading = true
      const params = {
        storeId: this.storeId,
        currentPage: this.currentPage,
        pageSize: this.pageSize
      }
      if (this.queryStatus) params.status = this.queryStatus
      if (this.queryOrderType) params.orderType = this.queryOrderType
      if (this.dateBegin) params.dateBegin = this.dateBegin
      if (this.dateEnd) params.dateEnd = this.dateEnd
      orderList(params).then(res => {
        if (res.code === 200 && res.data) {
          this.rows = res.data.rows || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '查询失败')
        }
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    handleTableChange(pag) {
      this.currentPage = pag.current
      this.load()
    },
    showDetail(record) {
      orderDetail(record.id).then(res => {
        if (res.code === 200 && res.data) {
          this.detailData = res.data
          this.detailVisible = true
        } else {
          this.$message.error(res.message || '查询详情失败')
        }
      }).catch(() => {})
    },
    showPrint(record) {
      orderPrintData(record.id).then(res => {
        if (res.code === 200 && res.data) {
          this.printText = JSON.stringify(res.data, null, 2)
          this.printVisible = true
        } else {
          this.$message.error(res.message || '查询打印数据失败')
        }
      }).catch(() => {})
    },
    handleRefund(record) {
      orderRefund(record.id).then(res => {
        if (res.code === 200) {
          this.$message.success('退款成功')
          this.load()
        } else {
          this.$message.error(res.message || '退款失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.manage-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #f0f2f5;
  z-index: 100;
  display: flex;
  flex-direction: column;
}
.manage-bar {
  height: 56px;
  flex-shrink: 0;
  background: #001529;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}
.manage-title {
  font-size: 17px;
  font-weight: 600;
}
.manage-wrap {
  flex: 1;
  overflow: auto;
  padding: 16px 20px;
}
.manage-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 4px;
}
.f-label {
  color: #555;
  font-size: 13px;
}
.act-btn {
  margin-left: 8px;
}
.detail-grid {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.d-item {
  width: 33.3%;
  font-size: 13px;
  color: #333;
  padding: 4px 0;
}
.d-k {
  color: #999;
  margin-right: 8px;
}
.print-pre {
  max-height: 60vh;
  overflow: auto;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
